import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "scada-plc-integration",
  title: "PLC & SCADA Protocol Integration",
  shortTitle: "SCADA Integration",
  kind: "horizontal",
  status: "draft",
  tier: "experience",
  seoTitle: "PLC & SCADA Protocol Integration | General Applications",
  seoDescription:
    "Industrial control protocol integration: bridging Modbus, OPC-UA, EtherNet/IP, and BACnet PLCs to modern telemetry, historian, and database layers.",
  summary:
    "Bridges between industrial control systems and modern software: protocol ingestion, historian and datastore integration, and the specifications that make a control-system upgrade reviewable before it is built.",
  image: "/images/blog/operator-workplaces-hero.webp",
  relatedCapabilitySlugs: ["automation-operations"],
  relatedIndustrySlugs: ["energy", "agriculture", "enterprise"],
  relatedPostSlugs: [
    "operator-workplaces",
    "interactive-industrial-schematics",
    "hot-cutover-realities",
    "interlocking-and-latency",
  ],
  engineering: [
    "Industrial protocol bridging across Modbus RTU/TCP, OPC-UA, EtherNet/IP, BACnet, and DNP3",
    "PLC registers, tag databases, and I/O mapping to relational and time-series datastores",
    "Edge gateway configuration, secure protocol translation, and air-gapped data diode topologies",
    "Historian integration and high-frequency real-time buffer management",
    "Control network segmentation, failover architecture, and communication watchdog monitoring",
    "Structured loop check, communication latency verification, and cutover commissioning",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Bridging the Gap Between Operational Protocols and Modern Software",
    },
    {
      type: "paragraph",
      text: "Industrial facilities and automated operations rely on programmable logic controllers (PLCs) and distributed I/O communicating over fieldbus protocols designed for determinism rather than web services. Connecting these operational networks to modern databases, analytics engines, and operator interfaces requires strict protocol translation without destabilizing scan cycles or violating network security boundaries.",
    },
    {
      type: "heading",
      level: 2,
      text: "Deterministic Ingestion with Explicit Exception Handling",
    },
    {
      type: "paragraph",
      text: "A durable protocol bridge does not merely poll tags; it manages communication failure modes, handles register endianness, enforces data typing, models link health, and isolates control networks from upstream disruptions. We engineer protocol interfaces that give engineers and operators clear visibility into plant telemetry with audit-ready configuration and zero impact on control loop execution.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit controller types, protocol dialects, register maps, network topology, scan rates, and upstream integration requirements.",
      produces: [
        "PLC and controller inventory with communication interface specifications",
        "Tag database, register mapping, and signal type classification",
        "Network topology, bandwidth, and security boundary review",
        "Data ingestion architecture and failure-mode recovery plan",
      ],
      fit: {
        good: [
          "Facilities operating mixed PLC ecosystems (Rockwell, Siemens, Schneider, AutomationDirect) requiring reliable data extraction",
          "Organizations bridging industrial telemetry into modern cloud, historian, or operational analytics platforms",
        ],
        poor: [
          "Requests to modify active safety PLC shutdown logic without certified plant safety engineer signoff",
          "Off-the-shelf consumer IoT sensor deployments with no industrial fieldbus protocols",
        ],
      },
    },
    "initial-design": {
      focus:
        "Architect edge gateway topologies, protocol translation pipelines, tag naming standards, and data contract definitions.",
      produces: [
        "Protocol translation and edge gateway architectural design",
        "Canonical tag naming convention and data schema specifications",
        "Network segmentation, firewall rules, and isolation specifications",
        "Historian and database ingestion interface designs",
      ],
    },
    "detailed-design": {
      focus:
        "Specify register address tables, polling schedules, edge transform rules, and communication watchdog logic.",
      produces: [
        "Point-to-point register address and polling schedule matrices",
        "Edge transformation, engineering unit conversion, and deadband specs",
        "Connection watchdog, heartbeat, and failover logic definitions",
        "Factory acceptance and bench simulation test procedures",
      ],
    },
    implementation: {
      focus:
        "Deploy edge gateways, configure protocol drivers, validate tag data integrity, and commission live data pipelines.",
      produces: [
        "Deployed and configured industrial protocol gateways and drivers",
        "Live telemetry feeds validated against controller registers",
        "Automated communication status and link-health monitors",
        "As-built interface documentation and operating runbooks",
      ],
    },
  },
};
